import {
    ChevronDownIcon,
    ChevronRightIcon,
    DeleteIcon,
    EditIcon,
} from "@chakra-ui/icons";
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Collapse,
    Flex,
    Grid,
    Heading,
    HStack,
    IconButton,
    Input,
    Spinner,
    Stack,
    Text,
    Textarea,
    useDisclosure,
    useToast,
    VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    delTranscripts,
    getTranscripts,
    patchTranscripts,
    postTranscripts,
} from "../../api/transcriptions";

const PAGE_SIZE = 25;

const TranscriptPanel = ({ customerId }) => {
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTranscript, setNewTranscript] = useState({
    name: "",
    transcript: "",
  });
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const toast = useToast();

  const fetchTranscripts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTranscripts(customerId, page, PAGE_SIZE);
      setTranscripts(res.data.results);
      setTotalCount(res.data.count);
    } catch {
      toast({
        title: "Error loading transcripts",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, page, toast]);

  useEffect(() => {
    fetchTranscripts();
  }, [fetchTranscripts]);

  const createTranscript = useCallback(async () => {
    if (!newTranscript.name.trim() || !newTranscript.transcript.trim()) {
      toast({
        title: "Please fill in both fields",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      const res = await postTranscripts(customerId, newTranscript);
      if (page === 1) {
        setTranscripts((prev) => [res.data, ...prev.slice(0, PAGE_SIZE - 1)]);
      } else {
        setPage(1);
      }
      setNewTranscript({ name: "", transcript: "" });
      toast({ title: "Transcript added", status: "success", duration: 2000 });
    } catch (error) {
      toast({
        title: "Failed to add transcript",
        description: error.response?.data
          ? JSON.stringify(error.response.data)
          : undefined,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [customerId, newTranscript, page, toast]);

  const updateTranscript = useCallback(
    async (id, updatedFields, onComplete) => {
      if (!updatedFields.name.trim() || !updatedFields.transcript.trim()) {
        toast({
          title: "Name and transcript cannot be empty",
          status: "warning",
          duration: 2000,
          isClosable: true,
        });
        return;
      }
      try {
        await patchTranscripts(customerId, id, updatedFields);
        setTranscripts((prev) =>
          prev.map((t) => (t.id === id ? { ...t, ...updatedFields } : t))
        );
        toast({
          title: "Transcript updated",
          status: "success",
          duration: 2000,
        });
        if (onComplete) onComplete();
      } catch {
        toast({ title: "Update failed", status: "error", duration: 2000 });
      }
    },
    [customerId, toast]
  );

  const deleteTranscript = useCallback(
    async (id) => {
      try {
        await delTranscripts(customerId, id);

        fetchTranscripts();
        toast({ title: "Transcript deleted", status: "info", duration: 2000 });
      } catch {
        toast({ title: "Delete failed", status: "error", duration: 2000 });
      }
    },
    [customerId, toast, fetchTranscripts]
  );

  if (loading) return <Spinner size="xl" />;

  const totalPages = totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 1;

  return (
    <Box p={4} maxW="100%" mx="auto">
      <Box
        borderWidth="1px"
        borderRadius="md"
        p={3}
        bg="white"
        boxShadow="sm"
        _hover={{ boxShadow: "md" }}
        mb={6}
      >
        <Heading size="md" mb={4}>
          Transcripts
        </Heading>

        <Stack spacing={3} bgColor={"#FFFFFF"}>
          <Input
            placeholder="Transcript Name"
            value={newTranscript.name}
            onChange={(e) =>
              setNewTranscript((prev) => ({ ...prev, name: e.target.value }))
            }
            size="sm"
          />
          <Textarea
            placeholder="Transcript Content"
            value={newTranscript.transcript}
            rows={3}
            onChange={(e) =>
              setNewTranscript((prev) => ({
                ...prev,
                transcript: e.target.value,
              }))
            }
            size="sm"
          />
          <Button
            colorScheme="blue"
            onClick={createTranscript}
            maxW="120px"
            size="sm"
            alignSelf="flex-start"
          >
            Add
          </Button>
        </Stack>
      </Box>

      {transcripts.length === 0 ? (
        <Text color="gray.500" fontStyle="italic" fontSize="sm">
          No transcripts found.
        </Text>
      ) : (
        <VStack spacing={4} align="stretch">
          {transcripts.map((t) => (
            <EditableTranscript
              key={t.id}
              transcript={t}
              onSave={updateTranscript}
              onDelete={() => deleteTranscript(t.id)}
            />
          ))}
        </VStack>
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <HStack justify="center" mt={6} spacing={4}>
          <Button
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <Text>
            Page {page} of {totalPages}
          </Text>
          <Button
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </HStack>
      )}
    </Box>
  );
};

const EditableTranscript = React.memo(({ transcript, onSave, onDelete }) => {
  const [editData, setEditData] = useState({
    name: transcript.name,
    transcript: transcript.transcript,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  useEffect(() => {
    setEditData({
      name: transcript.name,
      transcript: transcript.transcript,
    });
  }, [transcript]);

  const isDirty = useMemo(() => {
    return (
      editData.name !== transcript.name ||
      editData.transcript !== transcript.transcript
    );
  }, [editData, transcript]);

  const handleSave = async () => {
    if (!editData.name.trim() || !editData.transcript.trim()) {
      toast({
        title: "Name and transcript cannot be empty",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    await onSave(transcript.id, editData, () => {
      setIsEditing(false);
      setSaving(false);
    });
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={3}
      bg="white"
      boxShadow="sm"
      _hover={{ boxShadow: "md" }}
    >
      <Grid templateColumns="1fr auto auto" alignItems="center" gap={3}>
        {!isEditing ? (
          <>
            <Flex
              align="center"
              cursor="pointer"
              onClick={() => setShowContent((show) => !show)}
              userSelect="none"
            >
              {showContent ? (
                <ChevronDownIcon boxSize={5} mr={2} />
              ) : (
                <ChevronRightIcon boxSize={5} mr={2} />
              )}
              <Text fontWeight="semibold" noOfLines={1}>
                {transcript.name}
              </Text>
            </Flex>
            <IconButton
              aria-label="Edit transcript"
              icon={<EditIcon />}
              size="sm"
              onClick={() => setIsEditing(true)}
              variant="ghost"
            />
            <IconButton
              aria-label="Delete transcript"
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              onClick={onOpen}
              variant="ghost"
            />
          </>
        ) : (
          <Text fontWeight="semibold" gridColumn="1 / 4">
            Editing Transcript
          </Text>
        )}
      </Grid>

      <Collapse in={showContent} animateOpacity mt={2} mb={isEditing ? 3 : 0}>
        {!isEditing && (
          <Box
            whiteSpace="pre-wrap"
            maxH="180px"
            overflowY="auto"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            m={"5px"}
            p={2}
            fontSize="sm"
            bg="gray.50"
          >
            {transcript.transcript}
          </Box>
        )}
      </Collapse>

      {isEditing && (
        <Stack spacing={2} p={"20px"}>
          <Input
            value={editData.name}
            onChange={(e) =>
              setEditData((d) => ({ ...d, name: e.target.value }))
            }
            placeholder="Transcript Name"
            isDisabled={saving}
            size="sm"
          />
          <Textarea
            value={editData.transcript}
            onChange={(e) =>
              setEditData((d) => ({ ...d, transcript: e.target.value }))
            }
            rows={4}
            isDisabled={saving}
            size="sm"
          />
          <HStack justify="flex-end" spacing={2}>
            <Button
              size="sm"
              colorScheme="green"
              onClick={handleSave}
              isLoading={saving}
              isDisabled={!isDirty}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditData({
                  name: transcript.name,
                  transcript: transcript.transcript,
                });
                setIsEditing(false);
              }}
              isDisabled={saving}
            >
              Cancel
            </Button>
          </HStack>
        </Stack>
      )}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Transcript
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete &quot;{transcript.name}&quot;?
              This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} size="sm">
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                ml={3}
                size="sm"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
});

export default TranscriptPanel;
